/**
 * `/rezerwacje` — the trainer's session calendar: the title and the four-tab strip on ONE row, and
 * under it the tab. The A1 canon stacks an eyebrow, the title and the strip; the owner traded that
 * height for the grid on 25 September 2026 (about 90px at 1512×1000). The eyebrow went — the
 * navigation already names the module.
 *
 * The title is the module's, „Rezerwacje", on every tab — the canon's per-tab titles („Kiedy
 * pracuję", „Ustawienia rezerwacji", „Rozliczenia", „Kalendarz sesji") changed the title's width,
 * and the strip beside it moved with every switch (owner, 27 September 2026). They name the
 * browser tab instead.
 * The row's right end is a slot a tab portals into: the calendar's `+`, Grafik's zone,
 * Rozliczenia's „Rozliczam żetonami" switch.
 * It belongs to the header's row; what stands in it belongs to the tab's state.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';
import shell from '~/shared/ui/canonScreen.module.css';
import { TabStrip, tabButtonId, tabPanelId } from '~/shared/ui/TabStrip';

import { BOOKING_TABS, type BookingTab } from '../model/bookingTabs';
import { CalendarTab } from './CalendarTab';
import { LedgerTab } from './LedgerTab';
import { ScheduleTab } from './ScheduleTab';
import { SettingsTab } from './SettingsTab';
import styles from './BookingScreen.module.css';

interface BookingScreenProps {
  readonly tab: BookingTab;
  readonly onSelectTab: (tab: BookingTab) => void;
  /** The client the address asks Rozliczenia to open on (`?klient=`), or `null`. */
  readonly client: number | null;
  /** A client opened on Rozliczenia, written back into the address. */
  readonly onOpenClient: (customerId: number) => void;
}

export function BookingScreen({ tab, onSelectTab, client, onOpenClient }: BookingScreenProps) {
  const { t } = useTranslation();
  // ONE title for the module, whichever tab is open (owner, 27 September 2026): a title that
  // changed with the tab changed width, and the tab strip beside it jumped on every switch. The
  // tab names the section; the browser tab's title still says which one.
  const title = t('web:nav.booking');
  useDocumentTitle(
    tab === 'grafik' || tab === 'ustawienia' || tab === 'rozliczenia'
      ? t(`web:booking.pageTitle.${tab}`)
      : t('web:booking.title'),
  );
  const [actions, setActions] = useState<HTMLDivElement | null>(null);

  return (
    <main className={shell.page}>
      <div className={styles.header}>
        {/* `AppShell` focuses the screen's `h1` after a route change, so it stands in every state. */}
        <h1 className={styles.title}>{title}</h1>
        <TabStrip
          className={styles.tabBar}
          label={t('web:booking.tabsLabel')}
          tabs={BOOKING_TABS}
          selected={tab}
          renderLabel={(entry) => t(`web:booking.tabs.${entry}`)}
          onSelect={onSelectTab}
        />
        <div ref={setActions} className={styles.actions} />
      </div>

      <div
        className={styles.panel}
        role="tabpanel"
        id={tabPanelId(tab)}
        aria-labelledby={tabButtonId(tab)}
      >
        {tab === 'kalendarz' ? (
          <CalendarTab headerActions={actions} />
        ) : tab === 'grafik' ? (
          <ScheduleTab headerActions={actions} />
        ) : tab === 'ustawienia' ? (
          <SettingsTab
            onOpenSchedule={() => {
              onSelectTab('grafik');
            }}
          />
        ) : (
          <LedgerTab headerActions={actions} client={client} onOpenClient={onOpenClient} />
        )}
      </div>
    </main>
  );
}
